<script lang="ts" setup>
import { computed, onMounted, PropType, ref, toRef, useSlots, watch } from "vue"
import PaginationControl from "./PaginationControl.vue"
import { PermissionAction } from "@shared/components/core/helpers/securityTypes"
import { useI18n } from "vue-i18n"
import VaIconMagnifier from "@shared/components/icons/VaIconMagnifier.vue"
import { ItemsFunction } from "./types/paginationTypes"
import AuditFieldsModal from "@shared/components/AuditFieldsModal.vue"
import { hasAuditFields } from "@shared/helpers/utils"

const { t } = useI18n()
const sortBy = ref("")
const sortingOrder = ref<"asc" | "desc">("asc")
const localItems = ref<any[]>([])
const totalFiltered = ref(0)
const totalCount = ref(0)

const props = defineProps({
  columns: {
    type: Array as PropType<any[]>,
    required: true,
  },
  defaultSortBy: {
    type: String,
    default: "",
  },
  items: {
    type: [Array, Function] as PropType<any[] | ItemsFunction>,
    required: true,
  },
  title: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Object as PropType<{ page: number; perPage: number; total: number }>,
    required: false,
  },
  editLabel: {
    type: String,
    default: "",
  },
  deleteLabel: {
    type: String,
    default: "",
  },
  showEditButton: {
    type: [Boolean, Function] as PropType<boolean | ((rowData: any) => boolean)>,
    default: true,
  },
  showDeleteButton: {
    type: [Boolean, Function] as PropType<boolean | ((rowData: any) => boolean)>,
    default: true,
  },
  basePermission: {
    type: String,
    default: "",
  },
  exportTitle: {
    type: String,
    default: "",
  },
  secondaryActions: {
    type: Array as PropType<
      Array<{
        label: string
        icon: string
        action: (rowData: any) => void
        requiredPermission?: string
        requiredAction?: PermissionAction
        visible?: (rowData: any) => boolean
      }>
    >,
    default: () => [],
    validator: (actions: any[]) => actions.every((a) => typeof a.label === "string" && typeof a.action === "function"),
  },
  draggable: {
    type: Boolean,
    default: false,
  },
  showAll: {
    type: Boolean,
    default: false,
  },
  showAuditButton: {
    type: Boolean,
    default: true,
  },
})

const getValueByPath = (obj: any, path: string): any => {
  return path.split(".").reduce((o, p) => (o ? o[p] : "ZZZZZZ"), obj)
}

const formatCellValue = (col: any, item: any, forExport = false): string => {
  const raw = getValueByPath(item, col.key)

  const formatter = forExport ? col.exportFormatter ?? col.formatter : col.formatter

  if (typeof formatter === "function") {
    return String(formatter(raw, item) ?? "")
  }

  if (typeof raw === "boolean") {
    return t(raw ? "general.yes" : "general.no")
  }

  return String(raw ?? "")
}

const getCellChipColor = (col: any, item: any): string => {
  if (typeof col.chipColor === "function") {
    return col.chipColor(getValueByPath(item, col.key), item)
  }

  return col.chipColor ?? "primary"
}

const needsTruncation = (value: any, maxSize: number): boolean => String(value ?? "").length > maxSize

const truncateValue = (value: any, maxSize: number): string => {
  const str = String(value ?? "")
  return str.length > maxSize ? str.substring(0, maxSize) + "..." : str
}

const maxSizeColumns = computed(() => props.columns.filter((col) => col.maxSize && col.key !== "actions"))

const searchText = ref("")

const globallyFiltered = computed(() => {
  if (functionMode.value || !searchText.value?.trim()) return localItems.value
  const q = searchText.value.toLowerCase().trim()
  return localItems.value.filter((item) =>
    props.columns.some((col) => {
      if (col.key === "actions") return false
      return formatCellValue(col, item).toLowerCase().includes(q)
    }),
  )
})

const sortedItems = computed(() => {
  if (!sortBy.value) return globallyFiltered.value
  if (!globallyFiltered.value || globallyFiltered.value.length === 0) return []
  let sortKey = sortBy.value

  // get the key from columns based on the received sortBy value if there is a column with name property matching the sortBy value
  const column = props.columns.find((col) => col.name === sortBy.value)
  if (column && column.key) {
    sortKey = column.key
  }

  return [...globallyFiltered.value].sort((a, b) => {
    const aValue = getValueByPath(a, sortKey)
    const bValue = getValueByPath(b, sortKey)

    if (aValue < bValue) return sortingOrder.value === "asc" ? -1 : 1
    if (aValue > bValue) return sortingOrder.value === "asc" ? 1 : -1
    return 0
  })
})

const functionMode = computed<ItemsFunction | null>(() => {
  if (typeof props.items === "function") {
    return props.items
  }
  return null
})

const localLoading = toRef(props, "loading")
const localPagination = ref({ page: 1, perPage: 10, total: 0 })

const getDataFromFunction = async (query: string) => {
  if (!functionMode.value) return

  const data: any = await functionMode.value({
    searchText: query?.trim() ? query.trim() : null,
  })

  const items = data?.Items ?? data?.items ?? data?.result ?? (Array.isArray(data) ? data : [])
  const tf = data?.TotalFiltered ?? data?.totalFiltered ?? items.length
  const tc = data?.TotalCount ?? data?.totalCount ?? items.length

  localItems.value = items
  totalFiltered.value = Number(tf)
  totalCount.value = Number(tc)

  if (localPagination.value) {
    localPagination.value.total = items.length
    localPagination.value.page = 1
  }
}

const refresh = async () => {
  if (functionMode.value) {
    await getDataFromFunction(searchText.value)
  }
}

onMounted(() => {
  if (functionMode.value) {
    getDataFromFunction("")
  } else if (Array.isArray(props.items)) {
    // Applies column transforms like watch(props.items).
    // Prevents "[object Object]" on initial mount, since the watcher doesn't run yet.
    localItems.value = props.items
    localPagination.value.total = props.items.length
  }

  if (props.defaultSortBy) {
    const splittedValue = props.defaultSortBy.split(" ")
    if (splittedValue.length === 2) {
      sortBy.value = splittedValue[0]
      sortingOrder.value = splittedValue[1].toLowerCase() === "desc" ? "desc" : "asc"
    } else {
      sortBy.value = props.defaultSortBy
    }
  }
})

const shouldShowEditButton = (rowData: any): boolean => {
  if (typeof props.showEditButton === "function") {
    return props.showEditButton(rowData)
  }
  return Boolean(props.showEditButton)
}

const shouldShowDeleteButton = (rowData: any): boolean => {
  if (typeof props.showDeleteButton === "function") {
    return props.showDeleteButton(rowData)
  }
  return Boolean(props.showDeleteButton)
}

const showSecondaryActions = (rowData: any): boolean => {
  return props.secondaryActions.some((action) => (action.visible ? action.visible(rowData) : true))
}

// ── Audit fields modal ─────────────────────────────────────────────────────
const auditEntity = ref<Record<string, any> | null>(null)
const showAuditModal = ref(false)

const openAuditModal = (rowData: any) => {
  auditEntity.value = rowData
  showAuditModal.value = true
}

// ── Grouped mode ──────────────────────────────────────────────────────────

// If at least one column has grouped enabled, the grid will allow user to switch between modes
const groupByColumn = computed(() => props.columns.find((col) => col.grouped) ?? null)

const groupedNonActionsColumns = computed(() => props.columns.filter((col) => !col.grouped && col.key !== "actions"))

const hasActionsColumnInGrouped = computed(() => props.columns.some((col) => col.key === "actions"))
const actionsColumn = computed(() => props.columns.find((col) => col.key === "actions") ?? null)

// Group by the grouped column
const groupedItems = computed(() => {
  if (!groupByColumn.value) return []
  const groups = new Map<string, { key: string; items: any[] }>()
  for (const item of filteredByColumns.value) {
    const val = formatCellValue(groupByColumn.value, item)
    if (!groups.has(val)) groups.set(val, { key: val, items: [] })
    groups.get(val)!.items.push(item)
  }
  return Array.from(groups.values())
})

const expandedGroups = ref<Record<string, boolean>>({})
const groupMode = ref(true)

const toggleGroup = (key: string) => {
  expandedGroups.value = { ...expandedGroups.value, [key]: !expandedGroups.value[key] }
}

// ── Draggable row ordering ─────────────────────────────────────────────────
const draggingIndex = ref<number | null>(null)

const draggablePagedItems = computed(() => {
  if (props.draggable || props.showAll) return filteredByColumns.value
  const start = (localPagination.value.page - 1) * localPagination.value.perPage
  const end = start + localPagination.value.perPage
  return filteredByColumns.value.slice(start, end)
})

const SCROLL_ZONE = 80 // px from edge to trigger scroll
const SCROLL_SPEED = 12 // px per frame
let scrollRafId: number | null = null

const stopAutoScroll = () => {
  if (scrollRafId !== null) {
    cancelAnimationFrame(scrollRafId)
    scrollRafId = null
  }
}

const startAutoScroll = (direction: -1 | 1) => {
  stopAutoScroll()
  const step = () => {
    window.scrollBy(0, direction * SCROLL_SPEED)
    scrollRafId = requestAnimationFrame(step)
  }
  scrollRafId = requestAnimationFrame(step)
}

const onDragStart = (index: number) => {
  draggingIndex.value = index
}

const onDragOver = (e: DragEvent, index: number) => {
  e.preventDefault()
  const from = draggingIndex.value
  if (from === null || from === index) return

  const displayed = draggablePagedItems.value
  const localFrom = localItems.value.indexOf(displayed[from])
  const localTo = localItems.value.indexOf(displayed[index])
  if (localFrom === -1 || localTo === -1) return

  const items = [...localItems.value]
  const [moved] = items.splice(localFrom, 1)
  items.splice(localTo, 0, moved)
  localItems.value = items
  draggingIndex.value = index

  const fromTop = e.clientY
  const fromBottom = window.innerHeight - e.clientY
  if (fromTop < SCROLL_ZONE) {
    startAutoScroll(-1)
  } else if (fromBottom < SCROLL_ZONE) {
    startAutoScroll(1)
  } else {
    stopAutoScroll()
  }
}

const onDragEnd = () => {
  stopAutoScroll()
  emit("reorder", localItems.value)
  draggingIndex.value = null
}

// ── Column filters ─────────────────────────────────────────────────────────
const showColumnFilters = ref(false)
const columnFilterInputs = ref<Record<string, string>>({})
const columnFilters = ref<Record<string, string>>({})
const columnDebounceMap: Record<string, ReturnType<typeof setTimeout>> = {}

const onColumnFilterInput = (key: string, value: string) => {
  columnFilterInputs.value[key] = value
  clearTimeout(columnDebounceMap[key])
  columnDebounceMap[key] = setTimeout(() => {
    columnFilters.value = { ...columnFilters.value, [key]: value }
    localPagination.value.page = 1
  }, 200)
}

const toggleColumnFilters = () => {
  showColumnFilters.value = !showColumnFilters.value
  if (!showColumnFilters.value) {
    columnFilterInputs.value = {}
    columnFilters.value = {}
  }
}

const filteredByColumns = computed(() => {
  const activeFilters = Object.entries(columnFilters.value).filter(([, v]) => v?.trim())
  if (!activeFilters.length) return sortedItems.value

  return sortedItems.value.filter((item) =>
    activeFilters.every(([key, value]) => {
      const col = props.columns.find((c) => c.key === key)
      if (!col) return true

      const normalizedValue = value.toLowerCase().trim()

      const cellValue = getValueByPath(item, key)
      if (typeof cellValue === "boolean" && key.toLowerCase().includes("active")) {
        const displayValue = cellValue ? t("general.active") : t("general.inactive")
        return displayValue.toLowerCase().startsWith(normalizedValue)
      }

      const displayValue = formatCellValue(col, item).toLowerCase()
      return displayValue.includes(normalizedValue)
    }),
  )
})

// ── Search / pagination ────────────────────────────────────────────────────
const slots = useSlots()
const debounceId: { current: number | null } = { current: null }
const emit = defineEmits<{
  (event: "edit-row", row: any): void
  (event: "delete-row", profile: any): void
  (event: "reorder", items: any[]): void
}>()

let paginationInitialized = false
watch(
  () => props.pagination,
  (newPagination) => {
    if (!newPagination) return
    if (!paginationInitialized) {
      localPagination.value = { ...newPagination }
      paginationInitialized = true
    } else {
      // The parent only drives page/total; keep the user-selected page size so it
      // survives reloads (e.g. "Load Data" updating total resets it otherwise).
      localPagination.value = { ...newPagination, perPage: localPagination.value.perPage }
    }
  },
  { immediate: true, deep: true },
)

watch(searchText, (newValue) => {
  localPagination.value.page = 1
  if (functionMode.value) {
    if (debounceId.current) {
      clearTimeout(debounceId.current)
    }
    debounceId.current = window.setTimeout(() => {
      getDataFromFunction(newValue)
      debounceId.current = null
    }, 300)
  } else {
    localPagination.value.total = sortedItems.value.length
  }
})

watch(
  () => props.items,
  (newItems) => {
    if (!functionMode.value && Array.isArray(newItems)) {
      localItems.value = newItems
      localPagination.value.total = newItems.length
    } else if (functionMode.value) {
      getDataFromFunction(searchText.value)
    }
  },
  { immediate: true, deep: true },
)

const computedTotal = computed(() => filteredByColumns.value.length)

// When the number of records changes, navigate back to page 1 so the user is never
// stranded on a page that no longer exists (e.g. after filtering or reloading data).
watch(computedTotal, () => {
  localPagination.value.page = 1
})

const exportToCsv = () => {
  const exportColumns = props.columns.filter((col) => col.key !== "actions" && !col.notExportable)
  const header = exportColumns.map((col) => `"${(col.label ?? col.key).replace(/"/g, '""')}"`).join(",")
  const rows = filteredByColumns.value.map((item) =>
    exportColumns
      .map((col) => {
        const display = formatCellValue(col, item, true)
        return `"${display.replace(/"/g, '""')}"`
      })
      .join(","),
  )
  const csv = [header, ...rows].join("\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = `export_${props.exportTitle.replace(/"/g, '""') || "export"}_${new Date().toISOString()}.csv`
  link.click()

  URL.revokeObjectURL(url)
}

defineExpose({
  refresh,
})
</script>

<template>
  <AuditFieldsModal v-model="showAuditModal" :entity="auditEntity" />
  <div class="flex flex-col">
    <div class="flex flex-row justify-between items-center mb-4">
      <h6 v-if="props.title" class="va-h6">{{ props.title }}</h6>
      <slot name="appendTitle" />
      <div class="flex-1 flex justify-end items-stretch gap-2">
        <div
          v-if="groupByColumn && !props.draggable"
          class="flex rounded overflow-hidden border border-[var(--va-background-border)] px-3 gap-3"
        >
          <VaButton
            preset="plain"
            class="rounded-none h-full"
            :color="!groupMode ? 'primary' : 'secondary'"
            @click="groupMode = false"
          >
            <VaIcon name="mso-view_list" size="16px" class="mr-1" />{{ t("general.list") }}
          </VaButton>
          <VaButton
            preset="plain"
            class="rounded-none h-full"
            :color="groupMode ? 'primary' : 'secondary'"
            @click="groupMode = true"
          >
            <VaIcon name="mso-account_tree" size="16px" class="mr-1" />{{ t("general.group") }}
          </VaButton>
        </div>
        <VaInput v-model="searchText" :placeholder="t('general.search')" class="w-56" clearable>
          <template #prependInner>
            <VaIconMagnifier />
          </template>
        </VaInput>
      </div>
    </div>

    <!-- ── Draggable mode: custom table with row drag-and-drop ── -->
    <template v-if="props.draggable">
      <table class="w-full text-sm border-collapse">
        <thead class="border-b border-[var(--va-background-border)]">
          <tr>
            <th class="p-[0.625rem] w-8"></th>
            <th
              v-if="actionsColumn"
              class="p-[0.625rem] text-center whitespace-nowrap text-[0.625rem] leading-[1.6] font-bold uppercase tracking-[0.6px] text-[var(--va-text-primary)]"
              :style="actionsColumn.width ? `width:${actionsColumn.width}` : ''"
            >
              {{ actionsColumn.label }}
            </th>
            <th
              v-for="col in props.columns.filter((c) => c.key !== 'actions')"
              :key="col.key"
              class="p-[0.625rem] text-left text-[0.625rem] leading-[1.6] font-bold uppercase tracking-[0.6px] text-[var(--va-text-primary)]"
              :style="col.width ? `width:${col.width}` : ''"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in draggablePagedItems"
            :key="index"
            draggable="true"
            class="border-b border-[var(--va-background-border)] hover:bg-[var(--va-background-element)] transition-opacity"
            :class="{
              'opacity-30': draggingIndex === index,
              'cursor-grab': draggingIndex === null,
              'cursor-grabbing': draggingIndex !== null,
            }"
            @dragstart="onDragStart(index)"
            @dragover="(e) => onDragOver(e, index)"
            @dragend="onDragEnd"
          >
            <td class="py-2 pr-2 select-none text-[var(--va-secondary)] text-base leading-none">⠿</td>
            <td
              v-if="actionsColumn"
              class="py-2 pr-4 whitespace-nowrap"
              :style="actionsColumn.width ? `width:${actionsColumn.width}` : ''"
            >
              <div class="flex gap-1 items-center justify-center whitespace-nowrap">
                <VaButton
                  v-if="shouldShowEditButton(item)"
                  v-permission="[props.basePermission, PermissionAction.Write]"
                  v-tooltip="props.editLabel"
                  preset="primary"
                  size="small"
                  icon="mso-edit"
                  @click="emit('edit-row', item)"
                />
                <VaButton
                  v-if="props.showAuditButton && hasAuditFields(item)"
                  v-tooltip="t('general.auditFields')"
                  preset="primary"
                  size="small"
                  icon="manage_search"
                  color="secondary"
                  @click="openAuditModal(item)"
                />
                <VaButton
                  v-if="shouldShowDeleteButton(item)"
                  v-permission="[props.basePermission, PermissionAction.Delete]"
                  v-tooltip="props.deleteLabel"
                  preset="primary"
                  size="small"
                  icon="mso-delete"
                  color="danger"
                  @click="emit('delete-row', item)"
                />
                <slot name="actions-append" :row="item" />
                <VaMenu v-if="showSecondaryActions(item)">
                  <template #anchor>
                    <VaButton color="background-element" size="small">...</VaButton>
                  </template>
                  <VaValue v-for="(action, index) in props.secondaryActions" :key="index">
                    <VaMenuItem
                      v-if="action.visible ? action.visible(item) : true"
                      v-permission="[action.requiredPermission, action.requiredAction]"
                      :icon="action.icon"
                      @selected="() => action.action(item)"
                    >
                      {{ t(action.label) }}
                    </VaMenuItem>
                  </VaValue>
                </VaMenu>
              </div>
            </td>
            <td v-for="col in props.columns.filter((c) => c.key !== 'actions')" :key="col.key" class="py-2 pr-4">
              <slot :name="`cell(${col.key})`" :row-data="item">
                <VaChip v-if="col.chip" :color="getCellChipColor(col, item)" outline size="small">
                  {{ formatCellValue(col, item) }}
                </VaChip>
                <span
                  v-else-if="col.maxSize && needsTruncation(formatCellValue(col, item), col.maxSize)"
                  v-tooltip="formatCellValue(col, item)"
                  >{{ truncateValue(formatCellValue(col, item), col.maxSize) }}</span
                >
                <template v-else>{{ formatCellValue(col, item) }}</template>
              </slot>
            </td>
          </tr>
          <tr v-if="localItems.length === 0">
            <td :colspan="props.columns.length + 1" class="py-4 text-center text-[var(--va-secondary)] text-sm">
              {{ t("general.noData") }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- ── Grouped mode ── -->
    <template v-else-if="groupByColumn && groupMode">
      <table class="w-full text-sm border-collapse">
        <thead class="border-b border-[var(--va-background-border)]">
          <tr>
            <th class="p-[0.625rem] w-8"></th>
            <th
              class="p-[0.625rem] text-left text-[0.625rem] leading-[1.6] font-bold uppercase tracking-[0.6px] text-[var(--va-text-primary)]"
              :style="groupByColumn.width ? `width:${groupByColumn.width}` : ''"
            >
              {{ groupByColumn.label }}
            </th>
            <th
              v-for="col in groupedNonActionsColumns"
              :key="col.key"
              class="p-[0.625rem] text-left text-[0.625rem] leading-[1.6] font-bold uppercase tracking-[0.6px] text-[var(--va-text-primary)]"
              :style="col.width ? `width:${col.width}` : ''"
            >
              {{ col.label }}
            </th>
            <th v-if="hasActionsColumnInGrouped" class="p-[0.625rem] w-24"></th>
          </tr>
        </thead>
        <tbody>
          <template v-if="groupedItems.length === 0">
            <tr>
              <td
                :colspan="2 + groupedNonActionsColumns.length + (hasActionsColumnInGrouped ? 1 : 0)"
                class="py-4 text-center text-[var(--va-secondary)] text-sm"
              >
                {{ t("general.noData") }}
              </td>
            </tr>
          </template>
          <template v-for="group in groupedItems" :key="group.key">
            <tr
              class="border-b border-[var(--va-background-border)] cursor-pointer hover:bg-[var(--va-background-element)] font-semibold"
              @click="toggleGroup(group.key)"
            >
              <td class="py-2 pr-2 select-none text-[var(--va-secondary)] leading-none">
                <VaIcon :name="expandedGroups[group.key] ? 'mso-expand_more' : 'mso-chevron_right'" size="18px" />
              </td>
              <td
                :colspan="1 + groupedNonActionsColumns.length + (hasActionsColumnInGrouped ? 1 : 0)"
                class="py-2 pr-4"
              >
                <slot :name="`cell(${groupByColumn.key})`" :row-data="group.items[0]">
                  {{ formatCellValue(groupByColumn, group.items[0]) }}
                </slot>
                <VaBadge :text="String(group.items.length)" color="primary" class="ml-2" />
              </td>
            </tr>
            <template v-if="expandedGroups[group.key]">
              <tr
                v-for="(item, idx) in group.items"
                :key="idx"
                class="border-b border-[var(--va-background-border)] hover:bg-[var(--va-background-element)]"
              >
                <td></td>
                <td class="py-2 pr-4"></td>
                <td v-for="col in groupedNonActionsColumns" :key="col.key" class="py-2 pr-4">
                  <slot :name="`cell(${col.key})`" :row-data="item">
                    <VaChip v-if="col.chip" :color="getCellChipColor(col, item)" outline size="small">
                      {{ formatCellValue(col, item) }}
                    </VaChip>
                    <span
                      v-else-if="col.maxSize && needsTruncation(formatCellValue(col, item), col.maxSize)"
                      v-tooltip="formatCellValue(col, item)"
                      >{{ truncateValue(formatCellValue(col, item), col.maxSize) }}</span
                    >
                    <template v-else>{{ formatCellValue(col, item) }}</template>
                  </slot>
                </td>
                <td v-if="hasActionsColumnInGrouped" class="py-2">
                  <div class="flex gap-1 items-center">
                    <VaButton
                      v-if="shouldShowEditButton(item)"
                      v-permission="[props.basePermission, PermissionAction.Write]"
                      v-tooltip="props.editLabel"
                      preset="primary"
                      size="small"
                      icon="mso-edit"
                      @click="emit('edit-row', item)"
                    />
                    <VaButton
                      v-if="shouldShowDeleteButton(item)"
                      v-permission="[props.basePermission, PermissionAction.Delete]"
                      v-tooltip="props.deleteLabel"
                      preset="primary"
                      size="small"
                      icon="mso-delete"
                      color="danger"
                      @click="emit('delete-row', item)"
                    />
                    <slot name="actions-append" :row="item" />
                    <VaMenu v-if="showSecondaryActions(item)">
                      <template #anchor>
                        <VaButton color="background-element" size="small">...</VaButton>
                      </template>
                      <VaValue v-for="(action, index) in props.secondaryActions" :key="index">
                        <VaMenuItem
                          v-if="action.visible ? action.visible(item) : true"
                          v-permission="[action.requiredPermission, action.requiredAction]"
                          :icon="action.icon"
                          @selected="() => action.action(item)"
                        >
                          {{ t(action.label) }}
                        </VaMenuItem>
                      </VaValue>
                    </VaMenu>
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
      <div class="flex items-center justify-end mt-2">
        <VaButton preset="secondary" border-color="primary" round size="small" class="m-2" @click="exportToCsv">
          <VaIcon name="download" />
        </VaButton>
      </div>
    </template>

    <!-- ── Normal mode: VaDataTable ── -->
    <template v-else>
      <VaDataTable
        v-model:sort-by="sortBy"
        v-model:sorting-order="sortingOrder"
        :columns="props.columns"
        :items="filteredByColumns"
        :loading="localLoading"
        :per-page="localPagination.perPage"
        :current-page="localPagination.page"
        :default-sort-by="props.defaultSortBy"
        class="va-data-table"
      >
        <template #bodyAppend>
          <tr v-if="showColumnFilters" class="column-filters-row">
            <td v-for="col in props.columns" :key="col.key" class="column-filter-cell">
              <VaInput
                v-if="col.key !== 'actions' && col.filterable !== false"
                :model-value="columnFilterInputs[col.key] ?? ''"
                size="small"
                clearable
                :placeholder="col.label ?? col.key"
                class="w-full"
                @update:model-value="onColumnFilterInput(col.key, $event)"
              />
            </td>
          </tr>
        </template>

        <template
          v-for="col in props.columns.filter((c) => c.key !== 'actions')"
          :key="col.key"
          #[`cell(${col.key})`]="{ rowData }"
        >
          <slot :name="`cell(${col.key})`" :row-data="rowData">
            <VaChip v-if="col.chip" :color="getCellChipColor(col, rowData)" outline size="small">
              {{ formatCellValue(col, rowData) }}
            </VaChip>

            <template v-else>
              {{ formatCellValue(col, rowData) }}
            </template>
          </slot>
        </template>

        <template #cell(actions)="{ rowData }">
          <div class="flex w-full gap-2 items-center justify-center">
            <VaButton
              v-if="shouldShowEditButton(rowData)"
              v-permission="[props.basePermission, PermissionAction.Write]"
              v-tooltip="props.editLabel"
              preset="primary"
              size="small"
              icon="mso-edit"
              @click="emit('edit-row', rowData as any)"
            />
            <VaButton
              v-if="props.showAuditButton && hasAuditFields(rowData)"
              v-tooltip="t('general.auditFields')"
              preset="primary"
              size="small"
              icon="manage_search"
              color="secondary"
              @click="openAuditModal(rowData)"
            />
            <VaButton
              v-if="shouldShowDeleteButton(rowData)"
              v-permission="[props.basePermission, PermissionAction.Delete]"
              v-tooltip="props.deleteLabel"
              preset="primary"
              size="small"
              icon="mso-delete"
              color="danger"
              @click="emit('delete-row', rowData as any)"
            />
            <slot name="actions-append" :row="rowData" />
            <VaMenu v-if="showSecondaryActions(rowData)">
              <template #anchor>
                <VaButton color="background-element" size="small">...</VaButton>
              </template>
              <VaValue v-for="(action, index) in props.secondaryActions" :key="index">
                <VaMenuItem
                  v-if="action.visible ? action.visible(rowData) : true"
                  v-permission="[action.requiredPermission, action.requiredAction]"
                  :icon="action.icon"
                  @selected="() => action.action(rowData)"
                >
                  {{ t(action.label) }}
                </VaMenuItem>
              </VaValue>
            </VaMenu>
          </div>
        </template>
        <template v-for="col in maxSizeColumns" :key="`ms-${col.key}`" #[`cell(${col.key})`]="{ rowData }">
          <slot v-if="slots[`cell(${col.key})`]" :name="`cell(${col.key})`" :row-data="rowData" />
          <span
            v-else-if="needsTruncation(formatCellValue(col, rowData), col.maxSize)"
            v-tooltip="formatCellValue(col, rowData)"
            >{{ truncateValue(formatCellValue(col, rowData), col.maxSize) }}</span
          >
          <template v-else>{{ formatCellValue(col, rowData) }}</template>
        </template>
        <template
          v-for="col in Object.keys(slots).filter((s) => !maxSizeColumns.some((c) => `cell(${c.key})` === s))"
          :key="col"
          #[col]="slotProps"
        >
          <slot v-if="slots[col]" :name="col" v-bind="slotProps" />
        </template>
      </VaDataTable>

      <div class="flex items-center justify-between">
        <PaginationControl
          v-if="!draggable && !showAll"
          v-model:page="localPagination.page"
          v-model:per-page="localPagination.perPage"
          :total="computedTotal"
          :loading="$props.loading"
          class="flex-1"
        />
        <VaButton preset="secondary" border-color="primary" round size="small" class="m-2" @click="exportToCsv">
          <VaIcon name="download" />
        </VaButton>
        <VaButton
          v-if="!functionMode"
          preset="secondary"
          border-color="primary"
          round
          size="small"
          class="m-2"
          :color="showColumnFilters ? 'primary' : undefined"
          @click="toggleColumnFilters"
        >
          <VaIcon name="mso-filter_alt" />
        </VaButton>
      </div>

      <VaAlert
        v-if="functionMode && totalFiltered > localItems.length"
        type="info"
        class="mt-4 w-full"
        dense
        closeable
        border-color="info"
        border="left"
      >
        {{
          t("general.warnings.maxDisplayedRecords", {
            shown: localItems.length,
            filtered: totalFiltered,
          })
        }}
      </VaAlert>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.va-data-table {
  ::v-deep(.va-data-table__table-tr) {
    border-bottom: 1px solid var(--va-background-border);
  }

  ::v-deep(.va-checkbox__square) {
    border: 2px solid var(--va-primary) !important;
  }
}

.column-filter-cell {
  overflow: hidden;
  padding: 2px 4px;
  max-width: 0;

  ::v-deep(.va-input-wrapper) {
    min-height: 28px;
    height: 28px;
  }

  ::v-deep(.va-input-wrapper__field) {
    min-height: 28px;
    padding-top: 0;
    padding-bottom: 0;
  }
}

::v-deep(.actions-th),
::v-deep(.actions-td) {
  width: 1%;
  white-space: nowrap;
}
</style>
